/**
 * Analytics Service
 *
 * Wraps Firebase Analytics (GA4) with:
 *  - Safe, lazy initialization guarded by isSupported() so the app never
 *    crashes in Jest/Node or unsupported browsers.
 *  - Automatic PII stripping from every event payload.
 *  - Typed helper functions for every event in the project catalogue.
 *
 * Zero Firestore writes – all data goes via navigator.sendBeacon to GA4.
 */

import { getAnalytics, logEvent, setUserProperties, isSupported } from 'firebase/analytics';
import { app } from '../firebase/config';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Resolved analytics instance (null when unsupported or not yet initialised) */
let analyticsInstance = null;

/** Keys that may contain PII and must never leave the browser as analytics params */
const PII_KEYS = new Set([
  'email',
  'voornaam',
  'achternaam',
  'telefoon',
  'password',
  'adres',
  'geboortedatum',
  'nationaliteit',
  'naam',
]);

/**
 * Strip any PII keys from an event-params object.
 * Safe to call with null/undefined.
 * @param {Object|null|undefined} params
 * @returns {Object}
 */
export const sanitizeAnalyticsParams = (params) => {
  if (!params || typeof params !== 'object') return {};
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => !PII_KEYS.has(key))
  );
};

/**
 * Returns the initialised analytics instance, or null if unsupported.
 * Idempotent – safe to call multiple times.
 * @returns {Promise<import('firebase/analytics').Analytics|null>}
 */
export const getAnalyticsInstance = async () => {
  if (analyticsInstance !== null) return analyticsInstance;

  // measurementId must be present; skip silently if missing (e.g. emulator env)
  if (!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
    return null;
  }

  try {
    const supported = await isSupported();
    if (!supported) return null;
    analyticsInstance = getAnalytics(app);
    return analyticsInstance;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Track a GA4 page_view event.
 * @param {string} pagePath  e.g. '/catecheses'
 * @param {string} [pageTitle]
 */
export const trackPageView = async (pagePath, pageTitle) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;
  logEvent(analytics, 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track a custom application event.
 * Params are automatically sanitized to remove PII before sending.
 * @param {string} eventName
 * @param {Object} [params]
 */
export const trackEvent = async (eventName, params) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;
  logEvent(analytics, eventName, sanitizeAnalyticsParams(params));
};

/**
 * Set the user_role GA4 user property so every subsequent event is
 * attributed to the correct role cohort.
 * @param {'admin'|'verantwoordelijke'|'catechist'|'user'|null} role
 */
export const setUserRole = async (role) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;
  setUserProperties(analytics, { user_role: role || 'user' });
};

// ---------------------------------------------------------------------------
// Typed event helpers – one function per catalogue entry
// ---------------------------------------------------------------------------

// --- Authentication ---

/** Standard GA4 login event */
export const trackLogin = () => trackEvent('login', { method: 'password' });

/** Standard GA4 sign_up event */
export const trackSignUp = () => trackEvent('sign_up', { method: 'password' });

/** Admin approved a pending user */
export const trackUserApproved = () => trackEvent('user_approved');

/** Admin changed a user's role */
export const trackUserRoleChanged = () => trackEvent('user_role_changed');

// --- Catechese CRUD ---

/** @param {{ bisdom?: string, etappe?: string }} params */
export const trackCatecheseAdded = (params) =>
  trackEvent('catechese_added', params);

/** @param {{ bisdom?: string, etappe?: string }} params */
export const trackCatecheseEdited = (params) =>
  trackEvent('catechese_edited', params);

/** @param {{ bisdom?: string, etappe?: string }} params */
export const trackCatecheseDeleted = (params) =>
  trackEvent('catechese_deleted', params);

// --- Gemeenschap CRUD ---

/** @param {{ bisdom?: string }} params */
export const trackGemeenschapAdded = (params) =>
  trackEvent('gemeenschap_added', params);

/** @param {{ bisdom?: string }} params */
export const trackGemeenschapEdited = (params) =>
  trackEvent('gemeenschap_edited', params);

/** @param {{ bisdom?: string }} params */
export const trackGemeenschapDeleted = (params) =>
  trackEvent('gemeenschap_deleted', params);

// --- Conviventie CRUD ---

/** @param {{ locatie?: string, invited_groups_count?: number }} params */
export const trackConviventieAdded = (params) =>
  trackEvent('conviventie_added', params);

/** @param {{ locatie?: string, invited_groups_count?: number }} params */
export const trackConviventieEdited = (params) =>
  trackEvent('conviventie_edited', params);

/** @param {{ locatie?: string }} params */
export const trackConviventieDeleted = (params) =>
  trackEvent('conviventie_deleted', params);

// --- Lid (Member) CRUD ---

/** @param {{ status?: string, is_guest_group?: boolean }} params */
export const trackMemberAdded = (params) =>
  trackEvent('member_added', {
    ...params,
    is_guest_group: params?.is_guest_group ? 'true' : 'false',
  });

/** @param {{ status?: string, is_guest_group?: boolean }} params */
export const trackMemberEdited = (params) =>
  trackEvent('member_edited', {
    ...params,
    is_guest_group: params?.is_guest_group ? 'true' : 'false',
  });

/** @param {{ status?: string, is_guest_group?: boolean }} params */
export const trackMemberDeleted = (params) =>
  trackEvent('member_deleted', {
    ...params,
    is_guest_group: params?.is_guest_group ? 'true' : 'false',
  });

// --- Gastengroepen ---

/** Admin created a new guest group */
export const trackGuestGroupAdded = () =>
  trackEvent('guest_group_added');

/** @param {{ had_active_conviventies?: boolean }} params */
export const trackGuestGroupDeleted = (params) =>
  trackEvent('guest_group_deleted', {
    had_active_conviventies: params?.had_active_conviventies ? 'true' : 'false',
  });

// --- Business workflows ---

/**
 * Confirmation panel submitted.
 * @param {{ confirmed_count: number, has_notes_count: number }} params
 */
export const trackConviventieConfirmed = (params) =>
  trackEvent('conviventie_confirmed', params);

/**
 * Ledenlijst upload completed.
 * @param {{ row_count: number, target: 'gemeenschap'|'gastengroep' }} params
 */
export const trackLedenUploaded = (params) =>
  trackEvent('leden_uploaded', params);

/**
 * Ledenlijst upload parsing/upload failed.
 * @param {{ error_count: number }} params
 */
export const trackLedenUploadFailed = (params) =>
  trackEvent('leden_upload_failed', params);

/**
 * Ledenlijst downloaded as an Excel template (Feature 36).
 * `row_count` is 0 when the plain blank template was downloaded.
 * @param {{ row_count: number, target: 'gemeenschap'|'gastengroep' }} params
 */
export const trackLedenDownloaded = (params) =>
  trackEvent('leden_downloaded', params);

/**
 * Excel export triggered.
 * @param {{ export_type: 'catecheses'|'gemeenschappen'|'conviventies'|'leden', item_count: number }} params
 */
export const trackExportExecuted = (params) =>
  trackEvent('export_executed', params);

// --- Filters ---

/**
 * User changed a filter.
 * @param {{ filter_type: 'bisdom'|'source', filter_value: string }} params
 */
export const trackFilterChanged = (params) =>
  trackEvent('filter_changed', params);

// --- Diagnostics ---

/**
 * A user-visible error was caught and reported.
 *
 * Named `app_error` on purpose: GA4 reserves `error` (and `app_exception`), so a
 * custom event may not use those names. Only low-cardinality values are sent —
 * never the raw message, names or email addresses.
 *
 * @param {{ error_code: string, error_context: string, retryable?: boolean, app_version?: string }} params
 */
export const trackAppError = (params) =>
  trackEvent('app_error', {
    error_code: params?.error_code || 'unknown',
    error_context: params?.error_context || 'unknown',
    retryable: params?.retryable ? 'true' : 'false',
    ...(params?.app_version ? { app_version: params.app_version } : {}),
  });
